<template>
  <div>
    <b-row v-if="!currentRoom">
      <b-col
        v-if="!isLoading && !isLoadingRoomConnect"
        cols="12"
        md="6"
        offset-md="3"
        class="d-flex flex-column justify-content-center"
      >
        <h2>Livekit Videoconference</h2>
        <div>
          <label for="participantName">Votre nom</label>
          <input id="participantName" v-model="participantName" type="text" class="form-control" />
        </div>
        <div class="d-flex justify-content-end">
          <button
            id="connect-button"
            class="btn btn-primary mt-2"
            type="button"
            @click="connectWithFormInput()"
          >
            Connect
          </button>
        </div>
      </b-col>
      <appLoader v-if="isLoading" />
    </b-row>

    <b-row> <appLoader v-if="!currentRoom && isLoadingRoomConnect" /></b-row>
    <div
      v-show="currentRoom"
      class="video-conference"
      :style="`height: ${videoConferenceHeight}px;`"
    >
      <div class="video-conference-inner">
        <!-- NOTE: This will showed only when screen share is not active -->
        <div
          v-if="!isScreenShareEnabled"
          class="grid-layout-wrapper flex-column"
          :class="[isPaginationEnabled ? 'paginator' : '']"
        >
          <div
            v-for="pageNr of nrOfPages"
            v-show="!isScreenShareEnabled && pageNr == currentPageNr"
            :key="pageNr"
            class="grid-layout"
            :style="`--col-count: ${gridColsDefault}; --row-count: ${gridRowsDefault};`"
          >
            <div
              v-for="participant in getParticipantsByPage(pageNr)"
              :key="participant.sid"
              class="participant"
            >
              <video
                :id="`video-${participant.identity}`"
                :class="participant.isSpeaking ? 'speaking' : ''"
                style="width: 100%; height: 100%;"
              ></video>
              <audio :id="`audio-${participant.identity}`"></audio>
              <div class="metadata">
                <div class="metadata-item">
                  <span v-if="isMicrophoneEnabled(participant)" class="mic-on"
                    ><i class="bi bi-mic"></i
                  ></span>
                  <span v-else class="mic-off"><i class="bi bi-mic-mute"></i></span>
                  <div :id="`name-${participant.identity}`" class="name ml-1">
                    <span v-if="isLocalParticipant(participant)">
                      {{ participant.identity + "(you)" }}</span
                    >
                    <span v-else>{{ participant.identity }}</span>
                  </div>
                </div>
                <div
                  v-if="
                    isRemoteParticipant(participant) &&
                    (participant.isMicrophoneEnabled || participant.isCameraEnabled)
                  "
                  class="metadata-item"
                >
                  <div
                    v-if="participant.isMicrophoneEnabled"
                    :id="`disable-audio-button-${participant.sid}`"
                    class="p-1 mx-2"
                    type="button"
                    @click="disableParticipantTrack(participant, 'mic')"
                  >
                    <i class="bi bi-mic-mute-fill"></i>
                  </div>
                  <div
                    v-if="participant.isCameraEnabled"
                    :id="`disable-video-button-${participant.sid}`"
                    class="p-1 mx-2"
                    type="button"
                    @click="disableParticipantTrack(participant, 'cam')"
                  >
                    <i class="bi bi-camera-video-off-fill"></i>
                  </div>
                </div>
              </div>
              <div :id="`placeholder-${participant.identity}`" class="participant-placeholder">
                <i class="bi bi-person"></i>
              </div>
            </div>
          </div>
          <div v-show="isPaginationEnabled" class="pagination-control">
            <span type="button" class="bi bi-caret-left mr-2" @click="goPageBackward"></span>
            <span>{{ currentPageNr + " / " + nrOfPages }}</span>
            <span type="button" class="bi bi-caret-right ml-2" @click="goPageForward"></span>
          </div>
        </div>

        <!-- NOTE: Cannot v-if because we need "screenshare-area" id to exists -->
        <div
          v-show="isScreenShareEnabled"
          class="grid-layout-wrapper"
          :class="getClassForParticipantsOnShareScreen"
        >
          <!-- NOTE: We need to v-if grid-layout to prevent conflict when attach/render video -->
          <div
            v-if="isScreenShareEnabled"
            class="grid-layout screen-share"
            :style="`--col-count: ${gridColsDefault}; --row-count: ${gridRowsDefault};`"
          >
            <div
              v-for="participant in getParticipantsByPage(null)"
              :key="participant.sid"
              class="participant"
            >
              <video
                :id="`video-${participant.identity}`"
                :class="participant.isSpeaking ? 'speaking' : ''"
                style="width: 100%; height: 100%;"
              ></video>
              <audio :id="`audio-${participant.identity}`"></audio>
              <div class="metadata">
                <div class="metadata-item">
                  <span v-if="isMicrophoneEnabled(participant)" class="mic-on"
                    ><i class="bi bi-mic"></i
                  ></span>
                  <span v-else class="mic-off"><i class="bi bi-mic-mute"></i></span>
                  <div :id="`name-${participant.identity}`" class="name ml-1">
                    <span v-if="isLocalParticipant(participant)">
                      {{ participant.identity + "(you)" }}</span
                    >
                    <span v-else>{{ participant.identity }}</span>
                  </div>
                </div>
                <div
                  v-if="
                    isRemoteParticipant(participant) &&
                    (participant.isMicrophoneEnabled || participant.isCameraEnabled)
                  "
                  class="metadata-item"
                >
                  <div
                    v-if="participant.isMicrophoneEnabled"
                    :id="`disable-audio-button-${participant.sid}`"
                    class="p-1 mx-2"
                    type="button"
                    @click="disableParticipantTrack(participant, 'mic')"
                  >
                    <i class="bi bi-mic-mute-fill"></i>
                  </div>
                  <div
                    v-if="participant.isCameraEnabled"
                    :id="`disable-video-button-${participant.sid}`"
                    class="p-1 mx-2"
                    type="button"
                    @click="disableParticipantTrack(participant, 'cam')"
                  >
                    <i class="bi bi-camera-video-off-fill"></i>
                  </div>
                </div>
              </div>
              <div :id="`placeholder-${participant.identity}`" class="participant-placeholder">
                <i class="bi bi-person"></i>
              </div>
            </div>
          </div>
          <div id="screenshare-area">
            <div class="screenshare-metadata">
              <span id="screenshare-info"> </span>
              <span id="screenshare-resolution" hidden> </span>
            </div>
            <video id="screenshare-video" autoplay playsinline></video>
          </div>
        </div>

        <div class="control-bar">
          <div class="button-group">
            <button
              id="toggle-audio-button"
              class="btn btn-secondary mr-1"
              disabled
              type="button"
              @click="toggleAudio"
            >
              <span v-if="isLocalMicEnabled" class="mic-on">
                <i class="bi bi-mic"></i>
              </span>
              <span v-else class="mic-off"><i class="bi bi-mic-mute"></i></span>
              <span :class="showChat ? 'd-none' : 'd-none d-md-inline'" class="ml-1"
                >Microphone</span
              >
            </button>
            <div class="button-group-menu">
              <div class="btn btn-secondary button-menu" @click="toggleAudioInputList">
                <i class="bi bi-chevron-down"></i>
              </div>
              <div id="audio-input-list" class="device-menu">
                <ul class="device-list">
                  <li
                    v-for="audioInputOption in audioInputOptions"
                    :id="audioInputOption.deviceId"
                    :key="audioInputOption.id"
                    @click="handleDeviceSelected(audioInputOption.deviceId, 'audio-input')"
                  >
                    <span>{{ audioInputOption.text }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div class="button-group">
            <button
              id="toggle-video-button"
              class="btn btn-secondary mr-1"
              type="button"
              disabled
              @click="toggleVideo"
            >
              <span v-if="currentRoom && isCameraEnabled(currentRoom.localParticipant)">
                <i class="bi bi-camera-video"></i>
              </span>
              <span v-else>
                <i class="bi bi-camera-video-off"></i>
              </span>
              <span :class="showChat ? 'd-none' : 'd-none d-md-inline'" class="ml-1">Caméra</span>
            </button>
            <div class="button-group-menu">
              <div class="btn btn-secondary button-menu" @click="toggleVideoInputList">
                <i class="bi bi-chevron-down"></i>
              </div>
              <div id="video-input-list" class="device-menu">
                <ul class="device-list">
                  <li
                    v-for="videoInputOption in videoInputOptions"
                    :id="videoInputOption.deviceId"
                    :key="toString(videoInputOption)"
                    type="button"
                    @click="handleDeviceSelected(videoInputOption.deviceId, 'video-input')"
                  >
                    <span>{{ videoInputOption.text }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div class="button-single">
            <button
              id="share-screen-button"
              class="btn btn-secondary mr-1"
              disabled
              type="button"
              @click="shareScreen"
            >
              <span
                v-if="currentRoom && isParticipantScreenShareEnabled(currentRoom.localParticipant)"
              >
                <i class="bi bi-x-square"></i>
                <span class="d-none d-md-inline ml-1">Stop partager l'écran</span>
              </span>
              <span v-else>
                <i class="bi bi-box-arrow-in-up"></i>
                <span :class="showChat ? 'd-none' : 'd-none d-md-inline'" class="ml-1"
                  >Partager l'écran</span
                >
              </span>
            </button>
          </div>
          <div class="button-single">
            <div class="btn btn-secondary" @click="showChat = !showChat">
              <i class="bi bi-chat-left"></i>
              <span :class="showChat ? 'd-none' : 'd-none d-md-inline'" class="ml-1">Chat</span>
            </div>
          </div>
          <div class="button-single">
            <button
              id="disconnect-room-button"
              class="btn btn-danger"
              disabled
              type="button"
              @click="disconnectRoom"
            >
              <i class="bi bi-box-arrow-right"></i>
              <span :class="showChat ? 'd-none' : 'd-none d-md-inline'" class="ml-1"
                >Déconnecter</span
              >
            </button>
          </div>
        </div>
      </div>
      <div class="chat-wrapper" :class="showChat ? 'd-flex' : 'd-none'">
        <div class="chat-header d-flex justify-content-center align-items-center w-100">
          <div class="font-20 mt-1 mb-2">Messages</div>
          <div class="icon mt-1" type="button" @click="showChat = !showChat">
            <i class="bi bi-x"></i>
          </div>
        </div>
        <div class="chat-body w-100">
          <ul class="chat-messages">
            <li
              v-for="(chatMessage, index) in chatMessages"
              :key="index"
              class="chat-entry mb-1 p-1 mx-1 rounded"
            >
              <div class="meta d-flex justify-content-between">
                <span>{{ chatMessage.name }}</span>
                <span>{{ chatMessage.time }}</span>
              </div>
              <div class="message-body">{{ chatMessage.message }}</div>
            </li>
          </ul>
          <div class="d-flex p-2">
            <div class="mr-2">
              <input
                id="entry"
                type="text"
                class="form-control"
                placeholder="Type your message here"
              />
            </div>
            <div>
              <button
                id="send-button"
                class="btn btn-primary"
                type="button"
                disabled
                @click="enterText"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
const LIVEKIT_HOST = process.env.VUE_APP_LIVEKIT_HOST;

import User from "@/store/orm-axios/models/User";
import UserMW from "@/middlewares/User";
import LivekitAPI from "@/api/livekit";
import {
  Room,
  RoomEvent,
  VideoPresets,
  ScreenSharePresets,
  MediaDeviceFailure,
  RemoteParticipant,
  ParticipantEvent,
  LocalParticipant,
  ConnectionState,
  Track,
} from "livekit-client";
export default {
  props: {
    meetingId: {
      type: String,
      required: true,
    },
    livekitMeetingName: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      LocalParticipant,
      userId: Number(localStorage.getItem("userId")),
      windowInnerWidth: null,
      // NOTE: Default under 576px
      gridColsDefault: 1,
      gridRowsDefault: 2,
      videoConferenceHeight: 0,
      roomNrOfParticipants: 0,
      isLoading: false,
      roomName: null,
      participantName: "administrator",
      wsURL: LIVEKIT_HOST,
      token: null,
      // NOTE: This for administrator has to be true by default (allow to publish audio, video, screencast)
      shouldPublish: true,
      // preferredCodec: null,
      startAudioBtn: false,
      state: {
        isFrontFacing: false,
        encoder: new TextEncoder(),
        decoder: new TextDecoder(),
        defaultDevices: new Map(),
        bitrateInterval: undefined,
      },
      currentRoom: null,
      elementMapping: {
        "video-input": "videoinput",
        "audio-input": "audioinput",
        "audio-output": "audiooutput",
      },
      videoInputOptions: [],
      audioInputOptions: [],
      audioOutputOptions: [],
      chatMessages: [],
      isScreenShareEnabled: false,
      roomParticipants: [],
      maxNrOfParticipantsPerPage: 9,
      isPaginationEnabled: false,
      nrOfPages: 1,
      currentPageNr: 1,
      renderedVueParticipants: [],
      isLoadingRoomConnect: false,
      showChat: false,
      isLocalMicEnabled: false,
    };
  },
  computed: {
    audioOutputId() {
      return document.getElementById("audio-output");
    },
    user() {
      return User.find(this.userId) || {};
    },
    userFullName() {
      if (this.user && (this.user.lastname || this.user.firstname)) {
        let lastname = this.user.lastname ? this.user.lastname : "";
        let firstname = this.user.firstname ? this.user.firstname : "";
        return lastname + firstname;
      } else {
        return "Administrateur";
      }
    },
    getClassForParticipantsOnShareScreen() {
      if (window.innerWidth < 992 && this.isScreenShareEnabled) {
        return "flex-column-reverse";
      } else {
        return "flex-row";
      }
    },
  },
  watch: {
    "currentRoom.numParticipants"(newVal, oldVal) {
      if (newVal != oldVal) {
        this.roomNrOfParticipants = newVal;
        this.windowResizeEventHandler();
      }
    },
  },
  async created() {
    this.isLoading = true;
    await UserMW.GET(this.userId);

    this.acquireDeviceList();
    this.roomName = this.livekitMeetingName;
    this.participantName = this.userFullName;
    this.isLoading = false;
  },
  mounted() {
    window.addEventListener("resize", this.windowResizeEventHandler);
  },
  updated() {
    this.renderParticipantsVideo();
  },
  unmounted() {
    window.removeEventListener("resize", this.windowResizeEventHandler);
    this.renderedVueParticipants = [];
    this.roomParticipants = [];
  },
  methods: {
    windowResizeEventHandler() {
      this.windowInnerWidth = window.innerWidth;
      if (this.windowInnerWidth > 768) {
        this.maxNrOfParticipantsPerPage = 9;
        this.gridColsDefault = 3;
        const calculatedRows = Math.ceil(this.roomNrOfParticipants / 3);
        this.gridRowsDefault = calculatedRows <= 3 ? calculatedRows : 3;
      } else if (this.windowInnerWidth > 576) {
        this.maxNrOfParticipantsPerPage = 4;
        this.gridColsDefault = 2;
        const calculatedRows = Math.ceil(this.roomNrOfParticipants / 2);
        this.gridRowsDefault = calculatedRows <= 2 ? calculatedRows : 2;
      } else {
        this.maxNrOfParticipantsPerPage = 2;
        this.gridColsDefault = 1;
        this.gridRowsDefault = 2;
      }
    },
    initSizeCalculations() {
      const header = this.getElementFullHeight(document.getElementsByClassName("header")[0]);
      const content = this.getElementFullHeight(document.getElementsByClassName("content")[0]);
      const contentOnlyHeight = this.getElementOnlyHeight(
        document.getElementsByClassName("content")[0]
      );
      const footer = this.getElementFullHeight(document.getElementsByClassName("footer")[0]);
      const subHeader = this.getElementFullHeight(document.getElementsByClassName("sub-header")[0]);

      const totalHeight = header + content + footer;
      const contentHeightPadding = content - contentOnlyHeight;
      const meetingsContentBlankSpace = 50;
      return (
        totalHeight - header - subHeader - meetingsContentBlankSpace - contentHeightPadding - footer
      );
    },
    getElementFullHeight(element) {
      var elHeight = element.offsetHeight;
      elHeight += parseInt(window.getComputedStyle(element).getPropertyValue("margin-top"));
      elHeight += parseInt(window.getComputedStyle(element).getPropertyValue("margin-bottom"));
      return elHeight;
    },
    getElementOnlyHeight(element) {
      var elHeight = element.offsetHeight;
      elHeight -= parseInt(window.getComputedStyle(element).getPropertyValue("padding-top"));
      elHeight -= parseInt(window.getComputedStyle(element).getPropertyValue("padding-bottom"));
      return elHeight;
    },
    getAdminLivekitToken() {
      const roomName = this.roomName;
      const participantName = this.participantName;
      return new Promise((resolve, reject) => {
        LivekitAPI.getAdminToken(roomName, participantName, this.meetingId)
          .then(res => {
            if (res.data) {
              this.token = res.data;
              resolve(res);
            } else {
              this.token = null;
            }
          })
          .catch(err => {
            console.error(err);
            reject(err);
          });
      });
    },
    disableParticipantTrack(participant, trackType) {
      let track = null;
      if (trackType == "mic") {
        track = participant.getTrackPublication(Track.Source.Microphone);
      } else if (trackType == "cam") {
        track = participant.getTrackPublication(Track.Source.Camera);
      }

      if (!track) {
        return;
      }

      LivekitAPI.disableParticipantTrack(this.roomName, participant.identity, track.trackSid)
        .then(res => {
          if (res.data) {
            this.token = res.data;
          } else {
            this.token = null;
          }
        })
        .catch(err => {
          console.error(err);
        });
    },
    goPageForward() {
      this.currentPageNr =
        this.currentPageNr + 1 <= this.nrOfPages ? this.currentPageNr + 1 : this.nrOfPages;
    },
    goPageBackward() {
      this.currentPageNr = this.currentPageNr - 1 >= 1 ? this.currentPageNr - 1 : 1;
    },
    async connectWithFormInput() {
      this.videoConferenceHeight = this.initSizeCalculations();
      await this.getAdminLivekitToken();

      const roomOptions = {
        adaptiveStream: true,
        dynacast: true,
        audioOutput: {
          deviceId: this.audioOutputOptions[0] ? this.audioOutputOptions[0].deviceId : "default",
        },
        publishDefaults: {
          simulcast: true,
          videoSimulcastLayers: [VideoPresets.h90, VideoPresets.h216],
          videoCodec: "vp8",
          dtx: true,
          red: true,
          forceStereo: false,
          screenShareEncoding: ScreenSharePresets.h1080fps30.encoding,
        },
        videoCaptureDefaults: {
          resolution: VideoPresets.h720.resolution,
        },
      };

      await this.connectToRoom(roomOptions, {});
    },
    async connectToRoom(roomOptions, connectOptions) {
      this.isLoadingRoomConnect = true;
      const room = new Room(roomOptions);
      await room.prepareConnection(this.wsURL, this.token);

      room
        .on(RoomEvent.ParticipantConnected, this.participantConnected)
        .on(RoomEvent.ParticipantDisconnected, this.participantDisconnected)
        .on(RoomEvent.DataReceived, this.handleData)
        .on(RoomEvent.Disconnected, this.handleRoomDisconnect)
        .on(RoomEvent.LocalTrackPublished, () => {
          this.renderParticipantVue(room.localParticipant);
          this.renderScreenShare(room);
        })
        .on(RoomEvent.LocalTrackUnpublished, () => {
          this.renderParticipantVue(room.localParticipant);
          this.renderScreenShare(room);
        })
        .on(RoomEvent.MediaDevicesChanged, this.handleDevicesChanged)
        .on(RoomEvent.AudioPlaybackStatusChanged, () => {
          if (room.canPlaybackAudio) {
            this.startAudioBtn = true;
          } else {
            this.startAudioBtn = false;
          }
        })
        .on(RoomEvent.MediaDevicesError, e => {
          const failure = MediaDeviceFailure.getFailure(e);
          console.error(failure);
        })
        .on(RoomEvent.ConnectionQualityChanged, (quality, participant) => {
          this.renderParticipantVue(participant);
        })
        .on(RoomEvent.TrackSubscribed, (track, pub, participant) => {
          console.log("TrackSubscribed", track, pub, participant);
          this.renderParticipantVue(participant);
          this.renderScreenShare(room);

          console.log("participant audioLevel: ", participant.audioLevel);

          if (track.kind === "audio") {
            track.setVolume(1);
            const audioElm = document.getElementById(`audio-${participant.identity}`);
            if (audioElm) {
              const audioEl = track.attach(); // creates a new <audio> element
              audioEl.volume = 1;
              audioElm.appendChild(audioEl); // attach to your container element
            }

            console.log(track);
          }
        })
        .on(RoomEvent.TrackUnsubscribed, (_, pub, participant) => {
          this.renderParticipantVue(participant);
          this.renderScreenShare(room, true);
        })
        .on(RoomEvent.SignalConnected, async () => {
          // speed up publishing by starting to publish before it's fully connected
          // publishing is accepted as soon as signal connection has established
          if (this.shouldPublish) {
            // NOTE: !!!: This is returning errors
            // await room.localParticipant.enableCameraAndMicrophone();
          }
        })
        .on(RoomEvent.TrackMuted, (track, participant) => {
          this.renderParticipantVue(participant);
        })
        .on(RoomEvent.TrackUnmuted, (track, participant) => {
          this.renderParticipantVue(participant);
        });

      try {
        await room.connect(this.wsURL, this.token, connectOptions);
        this.isLoadingRoomConnect = false;
      } catch (error) {
        console.error(error);
        this.isLoadingRoomConnect = false;
        return;
      }
      this.currentRoom = room;
      window.currentRoom = room;
      this.setButtonsForState(true);

      room.remoteParticipants.forEach(participant => {
        this.participantConnected(participant);
      });
      this.participantConnected(room.localParticipant);

      return room;
    },
    isLocalParticipant(participant) {
      if (participant instanceof LocalParticipant) {
        return true;
      }
      return false;
    },
    isRemoteParticipant(participant) {
      if (participant instanceof RemoteParticipant) {
        return true;
      }
      return false;
    },
    isMicrophoneEnabled(participant) {
      const micPub = participant.getTrackPublication(Track.Source.Microphone);
      return micPub && micPub.isSubscribed && !micPub.isMuted;
    },
    isCameraEnabled(participant) {
      const cameraPub = participant.getTrackPublication(Track.Source.Camera);
      return cameraPub && cameraPub.isSubscribed && !cameraPub.isMuted;
    },
    isParticipantScreenShareEnabled(participant) {
      return participant.isScreenShareEnabled;
    },
    renderParticipantsVideo() {
      for (const participant of this.renderedVueParticipants) {
        try {
          const { identity } = participant;
          const videoElm = document.getElementById(`video-${identity}`);

          const cameraPub = participant.getTrackPublication(Track.Source.Camera);
          const cameraEnabled = cameraPub && cameraPub.isSubscribed && !cameraPub.isMuted;
          if (cameraEnabled) {
            document.getElementById(`placeholder-${identity}`).style.opacity = 0;
            cameraPub.videoTrack.attach(videoElm);
          } else {
            if (cameraPub && cameraPub.videoTrack) {
              // detach manually whenever possible
              cameraPub.videoTrack.detach(videoElm);
            } else {
              videoElm.src = "";
              videoElm.srcObject = null;
            }
            document.getElementById(`placeholder-${identity}`).style.opacity = 1;
          }
        } catch (error) {
          console.error(error);
        }
      }
    },
    getParticipantsByPage(pageNr) {
      if (pageNr == 1) {
        return this.renderedVueParticipants.slice(0, this.maxNrOfParticipantsPerPage);
      } else if (pageNr > 1) {
        const startIndex = (pageNr - 1) * this.maxNrOfParticipantsPerPage;
        const endIndex = startIndex + this.maxNrOfParticipantsPerPage;

        return this.renderedVueParticipants.slice(startIndex, endIndex);
      } else {
        return this.renderedVueParticipants;
      }
    },
    renderParticipantVue(participant, remove = false) {
      if (!this.currentRoom) return;

      let pageNr = 1;
      const index = this.renderedVueParticipants.findIndex(item => item.sid == participant.sid);
      if (index == "-1") {
        // NOTE: Add + 1 on length, because current participant will be added to the array
        pageNr = Math.ceil(
          (this.renderedVueParticipants.length + 1) / this.maxNrOfParticipantsPerPage
        );
        this.renderedVueParticipants.push(participant);
        this.nrOfPages = pageNr;
        this.isPaginationEnabled = pageNr > 1 ? true : false;
      } else {
        if (remove) {
          this.renderedVueParticipants.splice(index, 1);
          pageNr =
            this.renderedVueParticipants.length > 0
              ? Math.ceil(this.renderedVueParticipants.length / this.maxNrOfParticipantsPerPage)
              : 1;
          this.nrOfPages = pageNr;
          this.isPaginationEnabled = pageNr > 1 ? true : false;

          // NOTE: Prevent currentPage to remain on non existent page
          this.currentPageNr =
            this.currentPageNr > this.nrOfPages ? this.nrOfPages : this.currentPageNr;
        } else {
          // NOTE: UPDATE
          this.renderedVueParticipants.splice(index, 1, participant);
          pageNr = this.renderedVueParticipants[index].pageNr;
        }
      }

      if (participant instanceof LocalParticipant) {
        // NOTE: Enable/disable Mic
        const micPub = participant.getTrackPublication(Track.Source.Microphone);
        this.isLocalMicEnabled = micPub && micPub.isSubscribed && !micPub.isMuted;
      }
    },
    participantConnected(participant) {
      this.roomParticipants.push(participant);
      // NOTE: Render participant every time connects
      this.renderParticipantVue(participant);

      participant.on(ParticipantEvent.IsSpeakingChanged, () => {
        this.renderParticipantVue(participant);
      });
    },
    participantDisconnected(participant) {
      const participantIndex = this.roomParticipants.findIndex(item => item.sid == participant.sid);
      this.roomParticipants.splice(participantIndex, 1);
      this.renderParticipantVue(participant, true);
    },
    toggleAudio() {
      if (!this.currentRoom) return;
      const enabled = this.currentRoom.localParticipant.isMicrophoneEnabled;
      this.setButtonDisabled("toggle-audio-button", true);

      this.isLocalMicEnabled = !enabled;

      this.currentRoom.localParticipant.setMicrophoneEnabled(!enabled).catch(error => {
        console.error("Enable audio error: ", error);
        // NOTE: Revert
        this.isLocalMicEnabled = enabled;
      });

      this.setButtonDisabled("toggle-audio-button", false);
    },
    async toggleVideo() {
      if (!this.currentRoom) return;
      this.setButtonDisabled("toggle-video-button", true);
      const enabled = this.currentRoom.localParticipant.isCameraEnabled;

      try {
        await this.currentRoom.localParticipant.setCameraEnabled(!enabled);
      } catch (error) {
        console.error("Camera error: ", error);
      }

      this.setButtonDisabled("toggle-video-button", false);
      this.renderParticipantVue(this.currentRoom.localParticipant);
    },
    async shareScreen() {
      if (!this.currentRoom) return;
      const enabled = this.currentRoom.localParticipant.isScreenShareEnabled;

      this.setButtonDisabled("share-screen-button", true);
      try {
        await this.currentRoom.localParticipant.setScreenShareEnabled(!enabled, { audio: true });
      } catch (error) {
        console.log("Screen share canceled: ", error);
      }

      this.setButtonDisabled("share-screen-button", false);
    },
    enterText() {
      if (!this.currentRoom) return;
      const textField = document.getElementById("entry");
      if (textField.value) {
        const msg = this.state.encoder.encode(textField.value);
        this.currentRoom.localParticipant.publishData(msg, { reliable: true });

        this.chatMessages.push({
          name: this.currentRoom.localParticipant.identity,
          message: textField.value,
          time: this.$moment().format("LT"),
        });

        textField.value = "";
      }
    },
    disconnectRoom() {
      if (this.currentRoom) {
        this.renderedVueParticipants = [];
        this.roomParticipants = [];
        this.currentRoom.disconnect();
        this.currentRoom = null;
      }
    },
    handleData(msg, participant) {
      const message = this.state.decoder.decode(msg);

      this.chatMessages.push({
        name: participant.identity,
        message: message,
        time: this.$moment().format("LT"),
      });
    },
    handleRoomDisconnect() {
      if (!this.currentRoom) return;
      this.setButtonsForState(false);
      this.renderParticipantVue(this.currentRoom.localParticipant, true);
      this.currentRoom.remoteParticipants.forEach(p => {
        this.renderParticipantVue(p, true);
      });
      this.renderScreenShare(this.currentRoom);

      const container = document.getElementById("participants-area");
      if (container) {
        container.innerHTML = "";
      }

      // clear the chat area on disconnect
      const chat = document.getElementById("chat");
      if (chat) {
        chat.value = "";
      }
      this.currentRoom = undefined;
      window.currentRoom = undefined;
    },
    setButtonsForState(connected) {
      const connectedSet = [
        "toggle-video-button",
        "toggle-audio-button",
        "share-screen-button",
        "disconnect-room-button",
        "send-button",
      ];
      const disconnectedSet = ["connect-button"];

      const toRemove = connected ? connectedSet : disconnectedSet;
      const toAdd = connected ? disconnectedSet : connectedSet;

      toRemove.forEach(id => {
        if (document.getElementById(id)) {
          document.getElementById(id).removeAttribute("disabled");
        }
      });
      toAdd.forEach(id => {
        if (document.getElementById(id)) {
          document.getElementById(id).setAttribute("disabled", "true");
        }
      });
    },
    setButtonDisabled(buttonId, isDisabled) {
      const el = document.getElementById(buttonId);
      el.disabled = isDisabled;
    },
    renderScreenShare(room, isRemoteTrackUnsubscribed = false) {
      // NOTE: isRemoteTrackUnsubscribed = it means that a remoteParticipant cancel the screenShare and we have to clear screenShare from this app
      if (room.state !== ConnectionState.Connected) {
        return;
      }
      let participant = undefined;
      let screenSharePub = room.localParticipant.getTrackPublication(Track.Source.ScreenShare);
      let screenShareAudioPub = undefined;
      if (!screenSharePub) {
        room.remoteParticipants.forEach(p => {
          if (screenSharePub) {
            return;
          }
          participant = p;
          const pub = p.getTrackPublication(Track.Source.ScreenShare);
          if (pub && pub.isSubscribed) {
            // NOTE: Confirm trackPublication
            let hasScreenShareTrack = false;
            for (const t of p.trackPublications.values()) {
              if (t.source === Track.Source.ScreenShare) {
                hasScreenShareTrack = true;
              }
            }
            screenSharePub = hasScreenShareTrack ? pub : null;
            // NOTE: Set remoteParticipant which is sharing the screen
            this.isRemoteParticipantScreenShare = screenSharePub ? true : false;
          }
          const audioPub = p.getTrackPublication(Track.Source.ScreenShareAudio);
          if (audioPub && audioPub.isSubscribed) {
            screenShareAudioPub = audioPub;
          }
        });
      } else {
        participant = room.localParticipant;
        this.isRemoteParticipantScreenShare = false;
      }
      const allowAttachScreenShareVideo =
        this.isRemoteParticipantScreenShare && isRemoteTrackUnsubscribed ? false : true;

      if (screenSharePub && participant && allowAttachScreenShareVideo) {
        this.isScreenShareEnabled = true;
        // NOTE: Change GRID
        this.gridColsDefault = 1;
        this.gridRowsDefault = 6;

        const videoElm = document.getElementById("screenshare-video");
        screenSharePub.videoTrack.attach(videoElm);
        if (screenShareAudioPub) {
          screenShareAudioPub.audioTrack.attach(videoElm);
        }
        videoElm.onresize = () => {
          this.updateVideoSize(videoElm, document.getElementById("screenshare-resolution"));
        };
        const infoElm = document.getElementById("screenshare-info");
        infoElm.innerHTML = `<i class="bi bi-box-arrow-in-up mr-1"></i> ${participant.identity} écran`;
      } else if (!screenSharePub || !participant || isRemoteTrackUnsubscribed) {
        this.isScreenShareEnabled = false;
        this.isRemoteParticipantScreenShare = false;
        this.windowResizeEventHandler();
      }
    },
    async handleDeviceSelected(deviceId, elementId) {
      const kind = this.elementMapping[elementId];
      if (!kind) {
        return;
      }
      this.state.defaultDevices.set(kind, deviceId);
      if (this.currentRoom) {
        await this.currentRoom.switchActiveDevice(kind, deviceId);
      }

      if (elementId == "audio-input") {
        this.toggleAudioInputList();
      }
    },
    async handleDevicesChanged() {
      Promise.all(
        Object.keys(this.elementMapping).map(async id => {
          const kind = this.elementMapping[id];
          if (!kind) {
            return;
          }
          const devices = await Room.getLocalDevices(kind);
          const element = document.getElementById(id);

          for (const device of devices) {
            if (id == "audio-input") {
              this.audioInputOptions.push({
                deviceId: device.deviceId,
                text: device.label,
              });
            }
            if (id == "video-input") {
              this.videoInputOptions.push({
                deviceId: device.deviceId,
                text: device.label,
              });
            }
            if (id == "audio-output") {
              this.audioOutputOptions.push({
                deviceId: device.deviceId,
                text: device.label,
              });
            }
          }
        })
      );
    },
    async acquireDeviceList() {
      this.handleDevicesChanged();
    },
    updateVideoSize(element, target) {
      target.innerHTML = `(${element.videoWidth}x${element.videoHeight})`;
    },
    toggleAudioInputList() {
      const el = document.getElementById("audio-input-list");
      el.style.top = "-" + el.offsetHeight + "px";
      const isVisible = el.checkVisibility({ visibilityProperty: true });
      if (isVisible) {
        el.style.visibility = "hidden";
      } else {
        el.style.visibility = "visible";
      }
    },
    toggleVideoInputList() {
      const el = document.getElementById("video-input-list");
      const isVisible = el.checkVisibility({ visibilityProperty: true });
      el.style.top = "-" + el.offsetHeight + "px";
      if (isVisible) {
        el.style.visibility = "hidden";
      } else {
        el.style.visibility = "visible";
      }
    },
  },
};
</script>
<style lang="scss">
.video-conference {
  position: relative;
  display: flex;
  align-items: stretch;

  .video-conference-inner {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 100%;

    .grid-layout-wrapper {
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: calc(100% - 4.5rem);

      &.paginator {
        //NOTE: Add 3.5 rem height of paginator
        height: calc(100% - 8rem);
      }

      #screenshare-area {
        height: 100%;
        position: relative;
        margin-top: 1.25rem;
        margin-bottom: 1.25rem;
        //display: none;
        video {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        }
        .screenshare-metadata {
          position: absolute;
          bottom: 0;
          #screenshare-info {
            display: flex;
            align-items: center;
            padding: 0.25rem;
            background-color: rgba(0, 0, 0, 0.7);
            border-radius: 0.25rem;
            color: white;
          }
        }
      }
      .grid-layout {
        --col-count: 1;
        --row-count: 1;
        --grid-gap: 0.5rem;
        display: grid;
        grid-template-columns: repeat(var(--col-count), minmax(0, 1fr));
        grid-template-rows: repeat(var(--row-count), minmax(0, 1fr));
        grid-gap: var(--grid-gap);
        width: 100%;
        height: 100%;
        max-width: 100%;
        max-height: 100%;
        padding: var(--grid-gap);
        &.screen-share {
          width: 200px;
          overflow-y: auto;
          overflow-x: hidden;
          display: flex;
          flex-direction: column;
        }

        .participant {
          position: relative;

          video {
            object-fit: cover;
            border-radius: 0.5rem;
          }
          &.speaking {
            border: 2px solid lightblue;
            border-radius: 0.5rem;
          }

          .participant-placeholder {
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: #1e1e1e;
            opacity: 0;
            transition: opacity 0.2s ease-in-out;
            pointer-events: none;
            border-radius: 0.5rem;

            i {
              height: auto;
              width: auto;
              padding: 10%;
              font-size: 5rem;
              color: white;
            }
          }

          .metadata {
            position: absolute;
            right: 0.25rem;
            bottom: 0.25rem;
            left: 0.25rem;
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            gap: 0.5rem;
            line-height: 1;
            z-index: 1;

            .metadata-item {
              display: flex;
              align-items: center;
              padding: 0.25rem;
              background-color: rgba(0, 0, 0, 0.5);
              border-radius: 0.25rem;
              color: white;
            }
          }
        }
      }

      .pagination-control {
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: black;
        color: white;
        border-radius: 0.5rem;
        height: 2.5rem;
        margin-bottom: 1rem;

        .bi {
          font-size: 1.5rem;
        }
      }
    }
    //--lk-control-bar-height
    .control-bar {
      display: flex;
      gap: 0.5rem;
      align-items: center;
      justify-content: center;
      padding: 0.75rem;
      border-top: 1px solid hsla(0, 0%, 100%, 0.1);
      height: 4.5rem;

      .button-group {
        display: inline-flex;
        align-items: stretch;

        .button-group-menu {
          position: relative;
          flex-shrink: 0;

          // TODO: See how to calculate top
          .device-menu {
            visibility: hidden;
            width: max-content;
            position: absolute;
            top: 0;
            left: 0;
            z-index: 5;
            min-width: 10rem;
            padding: 0.5rem;
            white-space: nowrap;
            background-color: white;
            border: 1px solid black;
            border-radius: 0.5rem;

            //box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.15);

            .device-list {
              list-style: none;
              margin: 0;
              padding: 0;
            }
          }
        }
      }
    }
  }
}

.chat-wrapper {
  display: none;
  flex-direction: column;
  justify-content: space-between;
  grid-template-rows: 70px 1fr 70px;
  width: 25rem;
  align-items: end;
  border: 1px solid lightgrey;
  border-radius: 0.5rem;

  .chat-header {
    position: relative;
    .icon {
      position: absolute;
      right: 0;
      transform: translateX(-50%);
      font-size: 2.5rem;
    }
  }

  .chat-messages {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    width: 100%;
    max-height: 100%;
    overflow: auto;

    .chat-entry {
      background-color: lightgray;
    }
  }
}

@media only screen and (max-width: 992px) {
  .video-conference .video-conference-inner .grid-layout-wrapper {
    #screenshare-area {
      height: calc(100% - 12rem);
    }
    .grid-layout.screen-share {
      width: 100%;
      overflow-y: hidden;
      overflow-x: auto;
      display: flex;
      flex-direction: row;
      .participant {
        min-width: 15rem;
      }
    }
  }
  .chat-wrapper {
    position: fixed;
    background-color: white;
    top: 3.2rem;
    right: 0;
    max-width: 100%;
    bottom: 0;
    z-index: 1;
    border-top: 0;
    border-radius: 0;
  }
}

@media only screen and (max-width: 576px) {
  .chat-wrapper {
    position: fixed;
    background-color: white;
    top: 3.2rem;
    right: 0;
    max-width: 100%;
    bottom: 0;
    z-index: 1;
    border-top: 0;
    width: 100%;
    border-radius: 0;

    .chat-messages {
      justify-content: center;
    }
  }
}
</style>
